import { useCallback, useState } from 'react';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, ContentCopy, Inventory2, OpenInNew } from '@mui/icons-material';
import { Button, Chip, IconButton, Tooltip } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { FilterableDataTable } from '../components/filterable-data-table';
import { LoadingState } from '../components/loading-state';
import {
  useGetContainerEntitiesQuery,
  useGetProviderInfoQuery,
} from '../store/hooks';
import { formatDateTime } from '../utils/formatting';

export default function ContainerEntitiesListPage() {
  const { providerId: urlProviderId, containerKey } = useParams<{
    providerId: string;
    containerKey: string;
  }>();

  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;
  const copy = useCopyToClipboard();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const { data: provider, isLoading: providerLoading } = useGetProviderInfoQuery(
    { providerId: providerId ?? '' },
    { skip: !providerId },
  );

  const { data: entitiesData, isLoading: entitiesLoading } = useGetContainerEntitiesQuery(
    {
      containerKey: containerKey ?? '',
      userId: providerId ?? '',
      organizationId: provider?.organization_id ?? '',
      page,
      perPage,
    },
    { skip: !providerId || !containerKey || !provider?.organization_id },
  );

  const getRowUrl = useCallback(
    (entity: { entity_id: string }) => {
      return `/admin-v2/provider/${encodeURIComponent(providerId ?? '')}` +
        `/containers/${containerKey}/${entity.entity_id}`;
    },
    [providerId, containerKey],
  );

  if (!providerId || !containerKey) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Missing parameters</h2>
            <p className="text-gray-600 mt-2">Provider ID and container key are required.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (providerLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading provider details..." />
        </main>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const columns = [
    {
      key: 'entity_id',
      label: 'Patient ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">
            {value}
          </span>
          <Tooltip title="Copy ID">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                copy(value, 'Patient ID');
              }}
            >
              <ContentCopy fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'assembled_at',
      label: 'Last Assembled',
      render: (value: string | null) => (
        <span className="text-sm text-gray-500">
          {value ? formatDateTime(value) : 'Never'}
        </span>
      ),
    },
    {
      key: 'stale',
      label: 'Stale',
      render: (value: boolean) => (
        <Chip
          label={value ? 'Stale' : 'Current'}
          color={value ? 'warning' : 'success'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      key: 'component_count',
      label: 'Components',
      render: (value: number) => (
        <span className="text-sm text-gray-900">{value ?? 0}</span>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
                  <Button
                    variant="text"
                    className="text-sm font-medium text-gray-700 hover:text-blue-600"
                  >
                    {provider.name}
                  </Button>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                  Containers ({containerKey})
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center">
                  <Inventory2 className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">
                    Container Entities
                  </h1>
                  <p className="text-gray-600">
                    {containerKey} containers for {provider.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    Provider ID: {provider.provider_id}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Total Entities</div>
                <div className="text-2xl font-bold text-gray-900">
                  {entitiesData?.total ?? 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <FilterableDataTable
          title="Container Entities"
          columns={columns as any}
          data={entitiesData?.items || []}
          total={entitiesData?.total}
          loading={entitiesLoading}
          page={page}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          getRowUrl={getRowUrl}
          emptyMessage="No container entities found for this provider"
        />
      </main>
    </div>
  );
}
